import * as React from 'react';
import { useEffect, useState } from 'react';

import { Hit } from '@algolia/client-search';
import PriceChangePlain from '@core/components/display/labels/PriceChangePlain';
import CompactNumber from '@core/components/display/numbers/CompactNumber';
import priceFormatter from '@core/utils/functions/priceFormatter';
import { AssetResponse } from '@research-api/utils/api/assets';
import { GlobalMarketData } from '@research-api/utils/api/research/GlobalMarketData';
import PostType from '@research-api/utils/api/typings/PostType';

import SectionHeader from '@/components/display/headers/SectionHeader';
import Newsletter from '@/components/pages/frontpage/components/Feature/Right/Newsletter';
import NewsSectionArticle from '@/components/pages/frontpage/components/News/NewsSectionArticle';
import PriceAboutDetail from '@/components/pages/price/components/Content/About/PriceAboutDetail';
import PriceAboutSummary from '@/components/pages/price/components/Content/About/PriceAboutSummary';
import FaqItem from '@/components/pages/price/components/Content/Faq/FaqItem';
import PriceBreadcrumb from '@/components/pages/price/components/Header/PriceBreadcrumb';
import PriceHead from '@/components/pages/price/components/Header/PriceHead';
import PriceSectionHeader from '@/components/pages/price/components/Header/PriceSectionHeader';
import PriceMarketData from '@/components/pages/price/components/MarketData/PriceMarketData';
import TopPriceInfoHeader from '@/components/pages/price/components/TopPriceInfoHeader';
import RecentResearchFetchable from '@/components/sections/Right/RecentResearchFetchable';
import RecentResearchHeader from '@/components/sections/Right/RecentResearchHeader';
import { AlgoliaPostHit } from '@/types/algolia/AlgoliaPostHit';
import { ArticleType } from '@/types/ArticleType';
import mapAlgoliaHits from '@/utils/functions/algolia/mapAlgoliaHits';
import queryAlgoliaPosts from '@/utils/functions/algolia/queryAlgoliaPosts';
import subscribeToNewsletter from '@/utils/functions/subscribeToNewsletter';
import { Category } from '@/utils/functions/wordpress/fetchCategories';
import UpcomingEvents from '@/components/data-fetchers/events/UpcomingEvents';
import PriceChartWithInfo from '@/components/pages/price/components/Content/PriceChartWithInfo';

type PricePageProps = {
    asset: AssetResponse;
    recentResearch: PostType | null;
    globalMarketHistory: GlobalMarketData[];
    readMoreArticles: ArticleType[];
    categories: Category[];
    sponsorName?: string;
};

const PricePageLayout = (props: PricePageProps) => {
    const { asset } = props;

    const [hits, setHits] = useState<ArticleType[]>(mapAlgoliaHits([]));

    // Fetch associated news articles through algolia
    useEffect(() => {
        queryAlgoliaPosts(asset.title, 8, 0)?.then(queryResults => {
            setHits(mapAlgoliaHits(queryResults.hits as Array<Hit<AlgoliaPostHit>>));
        });
    }, []);

    return (
        <main className="border-gray-200 w-full">
            <div className="p-6 lg:p-10">
                <div className="flex flex-col justify-start items-start flex-grow relative gap-2">
                    <PriceBreadcrumb title={asset.title} code={asset.code} />
                    <div className="flex justify-start items-center relative gap-2 py-2">
                        <TopPriceInfoHeader
                            className="text-4xl md:text-6xl"
                            title={asset.title}
                            code={asset.code}
                            imageUrl={asset.imageUrl}
                        />
                    </div>
                    <p className="text-lg lg:text-xl text-left text-gray-500">
                        Live {asset.title} price updates and the latest {asset.title} news.
                    </p>
                </div>
            </div>
            <div className="flex flex-wrap">
                <div className="item w-full lg:w-8/12 lg:border-r">
                    <PriceHead
                        sponsoredBy={
                            <a href="https://blockworksresearch.com/assets" rel="nofollow noreferrer" target="_blank">
                                Blockworks Research
                            </a>
                        }
                    />
                    <PriceChartWithInfo asset={asset} />
                    <main>
                        <PriceAboutSummary title={asset.title} code={asset.code} description={asset.description} />
                        <section className="">
                            <PriceAboutDetail code={asset.code} hidePageTitle />
                        </section>
                    </main>
                </div>
                <div className="item w-full lg:w-4/12 lg:pb-8">
                    <PriceMarketData
                        usd24hChange={asset.change.usd24h}
                        usd24hVolume={asset.marketcap.usdVolume24h}
                        usdMarketCap={asset.marketcap.usd}
                        circulatingSupply={asset.supply.circulating}
                        marketCapRank={asset.marketcap.rank}
                    />
                    <div className="hidden lg:block">
                        <Newsletter
                            title="Newsletter"
                            subTitle="Blockworks Daily"
                            description="Get the daily newsletter that helps thousands of investors understand the markets."
                            inputPlaceholder="Email address"
                            buttonText="subscribe"
                            onButtonClick={subscribeToNewsletter}
                            className="border-t md:border-0 lg:border-t md:border-l lg:border-l-0 md:w-1/2 lg:w-full"
                        />
                        <UpcomingEvents />
                        <RecentResearchHeader title={'recent research'} />
                        <RecentResearchFetchable limit={2} hideHeader />
                    </div>
                </div>
            </div>
            <div className="w-full">
                <PriceSectionHeader title={`${asset.title} Stats`} />
                <div className="grid grid-cols-1 lg:grid-cols-2 p-8 gap-8">
                    <FaqItem question={`What is the market cap of ${asset.title}?`}>
                        The current market cap of {asset.title} is $
                        <CompactNumber className="font-semibold" number={asset.marketcap.usd} />. A high market
                        capitalization implies that the asset is highly valued by the market.
                    </FaqItem>
                    <FaqItem question={`What is the current trading activity of ${asset.title}?`}>
                        Currently, <CompactNumber className="font-semibold" number={asset.marketcap.usdVolume24h} /> of{' '}
                        {asset.code} were traded within 24 hours of time. In other words,{' '}
                        <CompactNumber className="font-semibold" number={asset.marketcap.usdVolume24h} /> have changed
                        hands within the past 24 hours through trading. That&apos;s a change of{' '}
                        <PriceChangePlain
                            className="inline-flex"
                            priceChangePercent={asset.change.usd24h}
                            useColorCode
                        />
                        .
                    </FaqItem>
                    <FaqItem question={`What is the current price of ${asset.title}?`}>
                        The price of 1 {asset.title} currently costs{' '}
                        <span className="font-semibold">{priceFormatter(asset?.price?.usd || 0)}</span>.
                    </FaqItem>
                    <FaqItem question={`How many ${asset.title} are there?`}>
                        The current circulating supply of {asset.title} is{' '}
                        <CompactNumber number={asset.supply.circulating} />. This is the total amount of {asset.code}{' '}
                        that is available.
                    </FaqItem>
                    <FaqItem question={`What is the relative popularity of ${asset.title}?`}>
                        {asset.title} ({asset.code}) currently ranks{' '}
                        <span className="font-semibold">{asset.marketcap.rank}</span> among all known cryptocurrency
                        assets. The popularity is based on relative market cap.
                    </FaqItem>
                </div>
            </div>
            <div>
                <SectionHeader
                    className="border-b border-t"
                    title={`latest ${asset.title} news`}
                    linkTitle={
                        <span className="flex align-middle">
                            more from news
                            <svg
                                width={8}
                                height={4}
                                viewBox="0 0 8 4"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="ml-1 mt-1.5"
                                preserveAspectRatio="none"
                            >
                                <path d="M6.005 1.5H0V2.5H6.005V4L8 2L6.005 0V1.5Z" fill="#5637CD" />
                            </svg>
                        </span>
                    }
                    linkUrl="/news"
                />
                <div className="p-12">
                    <div className="grid gap-8 grid-cols-1 xs:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                        {hits.map(article => (
                            <NewsSectionArticle article={article} key={`NewsSectionArticle-${article.slug}`} />
                        ))}
                    </div>
                </div>
            </div>
        </main>
    );
};

export default PricePageLayout;
